// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import { NotificationService } from '@way/theme-host/services/notification.service';
import {
  ChampsPageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { ChampsPageFooterComponent } from '../shared/footer/footer.component';
import {
  SharedPackageSelectionModalComponent,
  SelectedPackage,
} from '@way/website-themes/themes/shared/package-selection-modal/package-selection-modal.component';
import { PACKAGE_ICON_FALLBACK } from '@way/theme-host/shared/constants/website-builder-assets';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

// Import WebsiteElement type from home-page-preview
export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'packages'
    | 'coupons'
    | 'fleets'
    | 'express-details';
  content: any;
  selected?: boolean;
}

@Component({
  selector: 'app-champs-express-wash-packages-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [
    CommonModule,
    ChampsPageHeaderComponent,
    ChampsPageFooterComponent,
    SharedPackageSelectionModalComponent,
  ],
  templateUrl: './express-wash-packages-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./express-wash-packages-preview.component.scss'],
})
export class ChampsExpressWashPackagesPreviewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  error: string | null = null;

  // Package selection modal
  showPackageSelectionModal = false;
  selectedPackageForModal: Package | null = null;

  // Page data from JSON
  headerData: any = null;
  packageSectionData: any = null;
  moreInformationSectionData: any = null;
  footerData: any = null;
  pageTitleData: any = null;

  // Loading and animation state
  isPageLoaded = false;
  private intersectionObserver?: IntersectionObserver;
  private animatedElements = new Set<Element>();

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private cdr: ChangeDetectorRef
  ) {}

  /**
   * Initializes the component: loads page content, manages the page loaded state, triggers change detection and starts scroll animations after short delays.
   * @example
   * gOnInit()
   * void
   * @param {{void}} {{_none}} - No parameters.
   * @returns {{void}} Void.
   **/
  ngOnInit(): void {
    this.loadPageContent();

    // Initialize page loading state
    this.isPageLoaded = false;

    // Simulate page load animation
    setTimeout(() => {
      this.isPageLoaded = true;
      this.cdr.detectChanges();
      // Initialize scroll animations after page loads
      setTimeout(() => this.initScrollAnimations(), 100);
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.animatedElements.clear();
  }

  /**
   * Initialize scroll animations using Intersection Observer
   */
  private initScrollAnimations(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return; // Fallback for browsers without IntersectionObserver
    }

    // Use requestAnimationFrame for smoother performance
    requestAnimationFrame(() => {
      // Create intersection observer for scroll animations with optimized settings
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            entries.forEach(entry => {
              if (entry.isIntersecting && !this.animatedElements.has(entry.target)) {
                entry.target.classList.add('animate-in');
                this.animatedElements.add(entry.target);
                // Unobserve after animation to improve performance
                this.intersectionObserver?.unobserve(entry.target);
              }
            });
          });
        },
        {
          threshold: [0.1, 0.2], // Multiple thresholds for smoother detection
          rootMargin: '0px 0px -80px 0px', // Reduced margin for better scroll performance
        }
      );

      // Observe all animatable elements with delay to avoid blocking initial render
      setTimeout(() => {
        const elements = document.querySelectorAll(
          '.express-wash-packages-preview .website-element'
        );
        elements.forEach(el => {
          // Check if element is already in viewport
          const rect = el.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;

          el.classList.add('scroll-animate');

          // If element is already visible, animate it immediately
          if (isInViewport) {
            requestAnimationFrame(() => {
              el.classList.add('animate-in');
              this.animatedElements.add(el);
            });
          } else {
            // Only observe if not already in viewport
            this.intersectionObserver?.observe(el);
          }
        });
      }, 300);
    });
  }

  /**
   * Handle Angular OnChanges lifecycle to respond to input changes: reload page content when pageData changes, update header navigation when pages or currentPageType change, and refresh package section data when elements change to pick up background updates.
   * @example
   * gOnChanges({ pageData: { /* ... */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of packageSectionData to get updated background color
      const sections = this.pageData?.page?.sections || this.pageData?.sections || {};
      this.packageSectionData = sections.package_section || {};
    }
  }

  /**
   * Load and initialize page content and section data from this.pageData, log extracted sections, update header navigation, and trigger package loading.
   * @example
   * loadPageContent()
   * undefined
   * @returns {{void}} No return value (void).
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('📦 Loading Express Wash Packages page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || this.pageData?.sections || {};

    this.headerData = sections.header || {};
    this.packageSectionData = sections.package_section || {};
    this.moreInformationSectionData = sections.more_information_section || {};
    this.footerData = sections.footer || {};
    // Load page-title section (for heading and description)
    this.pageTitleData = sections['page-title'] || sections.hero_section || {};

    this.logger.debug('Header data:', this.headerData);
    this.logger.debug('Package section data:', this.packageSectionData);
    this.logger.debug('More Information section data:', this.moreInformationSectionData);
    this.logger.debug('Footer data:', this.footerData);
    this.logger.debug('Page title data:', this.pageTitleData);

    // Update header navigation to match current pages
    this.updateHeaderNavigation();

    // Load packages from API
    this.loadPackages();
  }

  /**
   * Update the component's headerData navigation from pages and ensure a default CTA button exists.
   * @example
   * this.updateHeaderNavigation()
   * undefined
   * @returns {void} Builds headerData.navigation from visible pages (label, link, active) and sets a default headerData.ctaButton if missing.
   */
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Express Wash Packages:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Load car wash packages and update component state; fetches from packageService, filters packages to include only those with 'single_wash', and sets loading and error flags.
   * @example
   * this.loadPackages()
   * undefined
   * @returns {{void}} Updates component state (packages, loading, error) and returns nothing.
   **/
  private loadPackages(): void {
    this.loading = true;
    this.error = null;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('✅ Packages loaded:', response);
        if (response.status && response.data) {
          // Filter packages: exclude those that only have "monthly_membership"
          // Show packages that have "single_wash" (with or without "monthly_membership")
          this.packages = response.data.filter((pkg: Package) => {
            if (!pkg.wash_types || !Array.isArray(pkg.wash_types)) {
              return false; // Exclude packages without wash_types
            }

            // Include if wash_types includes "single_wash"
            // This covers both cases:
            // 1. ["single_wash"] - only single wash
            // 2. ["monthly_membership", "single_wash"] - both types
            // Excludes: ["monthly_membership"] - only monthly membership
            return pkg.wash_types.includes('single_wash');
          });
          this.logger.debug('Packages count (after filter):', this.packages.length);
          this.logger.debug(
            'Filtered packages:',
            this.packages.map(p => ({ id: p.id, name: p.package_name, wash_types: p.wash_types }))
          );
        } else {
          this.error = 'No packages available';
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('❌ Error loading packages:', err);
        this.error = 'Failed to load packages';
        this.loading = false;
      },
    });
  }

  // Format price for display
  formatPrice(price: number | string | undefined): string {
    if (!price) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `$${numPrice.toFixed(2)}`;
  }

  // Get package image or fallback
  getPackageImage(pkg: Package): string {
    // Use image from API if available, otherwise fall back to static placeholder
    if (pkg.image && pkg.image.trim() !== '') {
      return pkg.image;
    }
    return PACKAGE_ICON_FALLBACK;
  }

  // Get package image fit/scaletype
  /**
   * Determine the appropriate CSS object-fit value for a package image based on the package's scaletype configuration.
   * @example
   * etPackageImageFit(pkg)
   * 'cover'
   * @param {Package} pkg - Package object whose id is used to find scaletype configuration.
   * @returns {string} CSS object-fit value ('cover', 'contain', 'fill', or 'scale-down'), defaults to 'cover'.
   **/
  getPackageImageFit(pkg: Package): string {
    // Check package_section.packages array for this package's scaletype
    const packageSectionPackages = this.packageSectionData?.packages || [];
    const packageConfig = packageSectionPackages.find((p: any) => p.id === pkg.id);

    if (packageConfig?.scaletype) {
      // Map API scaletype to CSS object-fit values
      const scaletype = packageConfig.scaletype;
      if (scaletype === 'fill' || scaletype === 'stretch') {
        return 'fill';
      } else if (scaletype === 'cover') {
        return 'cover';
      } else if (scaletype === 'contain') {
        return 'contain';
      } else if (scaletype === 'fit') {
        return 'scale-down';
      }
    }

    // Default to cover if not specified
    return 'cover';
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = PACKAGE_ICON_FALLBACK;
  }

  selectedPlanType: Record<string, 'single' | 'monthly'> = {};

  // Check if package has single wash
  hasSingleWash(pkg: Package): boolean {
    return !!pkg.wash_types?.includes('single_wash');
  }

  // Check if package has monthly membership
  hasMonthlyMembership(pkg: Package): boolean {
    return !!pkg.wash_types?.includes('monthly_membership');
  }

  // Check if package has unlimited washes
  isUnlimited(pkg: Package): boolean {
    return pkg.type === 'unlimited' || pkg.package_name?.toLowerCase().includes('unlimited');
  }

  // Get selected plan type
  getSelectedPlanType(pkg: Package): 'single' | 'monthly' {
    if (this.selectedPlanType[pkg.id]) {
      return this.selectedPlanType[pkg.id];
    }
    // Default: if it has single, use single. Else if it has monthly, use monthly.
    if (this.hasSingleWash(pkg)) {
      return 'single';
    } else if (this.hasMonthlyMembership(pkg)) {
      return 'monthly';
    }
    return this.isUnlimited(pkg) ? 'monthly' : 'single';
  }

  // Select plan type
  selectPlanType(packageId: string, planType: 'single' | 'monthly', event: Event): void {
    event.stopPropagation();
    this.selectedPlanType[packageId] = planType;
    this.cdr.detectChanges();
  }

  // Get display price (use single_list_price or monthly_list_price)
  getListPrice(pkg: Package): string {
    const planType = this.getSelectedPlanType(pkg);
    return planType === 'monthly' ? pkg.monthly_list_price : pkg.single_list_price;
  }

  // Get strikeoff price (discounted price)
  getStrikeoffPrice(pkg: Package): string {
    const planType = this.getSelectedPlanType(pkg);
    return planType === 'monthly' ? pkg.monthly_strikeoff_price : pkg.single_strikeoff_price;
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  /**
   * Get package section element for selection
   */
  getPackageSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'packages' ||
        el.id === 'packages' ||
        (el as any).originalKey === 'package_section'
    );
  }

  /**
   * Get package section background color
   */
  getPackageSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const packageElement = this.getPackageSectionElement();
    const packageContent = packageElement?.content as any;
    if (packageContent?.backgroundColor) {
      return packageContent.backgroundColor;
    }
    // Priority 2: Check packageSectionData (from API/pageData)
    if (this.packageSectionData?.backgroundColor) {
      return this.packageSectionData.backgroundColor;
    }
    // Priority 3: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 4: Check pageData directly
    const sections = page?.sections || this.pageData?.sections || {};
    const packageSection = sections.package_section || {};
    if (packageSection.backgroundColor) {
      return packageSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get page title section background color (same as package section)
   */
  getPageTitleBackgroundColor(): string | null {
    // Use the same background color as the package section
    return this.getPackageSectionBackgroundColor();
  }

  /**
   * Get page title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Express Wash Packages';
  }

  /**
   * Get page subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Choose from our range of washes that suit all needs and budgets.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    // Handle both editor and preview formats
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Handle package section click
   */
  onPackageSectionClick(event: Event): void {
    // Don't handle section click if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer') ||
      target.closest('.page-title-header-wrapper')
    ) {
      return; // Let navigation handle the click
    }

    // Stop event propagation to prevent conflicts
    event.stopPropagation();

    const packageElement: WebsiteElement = {
      id: 'packages',
      type: 'packages',
      content: {
        ...(this.packageSectionData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        pageTitleData: this.pageTitleData,
      },
      selected: true,
      originalKey: 'package_section',
    } as any;

    this.logger.debug('📦 Package section clicked, emitting element:', packageElement);
    this.elementSelect.emit(packageElement);
  }

  /**
   * Retrieve the header logo object from the header element or page data, with sensible fallbacks.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Example', link: '/', alt: 'Example Logo' }
   * @param {void} none - No arguments.
   * @returns {Logo} Logo object containing image, text, link and alt.
   **/
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    // Priority 1: Check header element content (most up-to-date)
    if (headerContent?.logo) {
      // If logo has image/url, return it
      if (headerContent.logo.image || headerContent.logo.url) {
        return {
          image: headerContent.logo.image || headerContent.logo.url || '',
          text: headerContent.logo.text || 'Logo',
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
      // If logo has text but no image, return text logo
      if (headerContent.logo.text) {
        return {
          image: '',
          text: headerContent.logo.text,
          link: headerContent.logo.link || '/',
          alt: headerContent.logo.alt || 'Logo',
        };
      }
    }
    // Priority 2: Check branding data from pageData
    if (this.pageData?.json_data?.website?.branding?.logo) {
      const brandingLogo = this.pageData.json_data.website.branding.logo;
      if (brandingLogo.url || brandingLogo.image) {
        return {
          image: brandingLogo.url || brandingLogo.image || '',
          text: brandingLogo.text || 'Logo',
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
      if (brandingLogo.text) {
        return {
          image: '',
          text: brandingLogo.text,
          link: brandingLogo.link || '/',
          alt: brandingLogo.alt || 'Logo',
        };
      }
    }
    return { text: 'Logo', link: '/', image: '', alt: 'Logo' };
  }

  /**
   * Builds header navigation items from the component header content or the component pages, ensuring each item has a pageType (derived from explicit page mapping, link normalization, or matching pages).
   * @example
   * etHeaderNavigation()
   * [{ label: 'Home', link: '#home', pageType: 'home', active: true }]
   * @param {void} none - No parameters; uses the component instance state (this.getHeaderElement(), this.pages, this.currentPageType).
   * @returns {NavigationItem[]} Array of navigation items with label, link, pageType, and optional active flag.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      // Ensure navigation items have pageType set
      return headerContent.navigation.map((nav: any) => {
        // If pageType is not set, try to extract it from link or find matching page
        if (!nav.pageType) {
          const matchingPage = this.pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace(/^#+|\/+$/g, '').replace(/^\/+/, '')
          );
          if (matchingPage) {
            nav.pageType = (matchingPage as any).pageType;
          } else {
            // Extract from link
            const normalizedLink = (nav.link || '')
              .replace(/^#+|\/+$/g, '')
              .replace(/^\/+/, '')
              .toLowerCase();
            if (
              normalizedLink === 'expresswashes' ||
              normalizedLink === 'express-washes' ||
              normalizedLink.includes('express-wash')
            ) {
              nav.pageType = 'express-wash-packages';
            } else if (
              normalizedLink === 'expressdetails' ||
              normalizedLink === 'express-details' ||
              normalizedLink.includes('express-detail') ||
              normalizedLink.includes('express-auto')
            ) {
              nav.pageType = 'express-auto-details';
            } else if (
              normalizedLink === 'fleets' ||
              normalizedLink === 'fleet' ||
              normalizedLink.includes('fleet')
            ) {
              nav.pageType = 'fleet-management';
            } else if (
              normalizedLink === 'coupons' ||
              normalizedLink === 'coupon' ||
              normalizedLink.includes('coupon')
            ) {
              nav.pageType = 'coupons';
            } else if (
              normalizedLink === '' ||
              normalizedLink === 'home' ||
              normalizedLink === '/'
            ) {
              nav.pageType = 'home';
            } else {
              nav.pageType = normalizedLink || 'home';
            }
          }
        }
        return nav;
      });
    }
    // Fallback to generating from pages
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home', // Ensure pageType is set
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Get the header CTA button configuration from the template header content, falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton|null}} CTAButton object containing text, link, backgroundColor and textColor, or null if unavailable.
   **/
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    const primaryColor = this.getPrimaryColor();
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        // Use button's backgroundColor if provided, otherwise fall back to primaryColor
        backgroundColor: headerContent.ctaButton.backgroundColor || primaryColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: primaryColor,
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#6B7280';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Manrope';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get footer quick links from active pages
   * Filters out pages where visible === false (active === false)
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    // First, try to get from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Retrieve and normalize social link entries from the component footer content, handling multiple possible data shapes and providing sensible defaults.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'facebook', url: 'https://facebook.com/example', icon: 'facebook' }]
   * @param {{void}} {{none}} - No arguments.
   * @returns {{Array<{ platform: string; url: string; icon: string }>} } Return an array of social link objects each containing platform, url and icon.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    // Handle different data structures:
    // 1. socialLinks as array (from website builder)
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 2. socialLinks as object with platforms array (from website preview)
    if (
      footerContent?.socialLinks?.platforms &&
      Array.isArray(footerContent.socialLinks.platforms)
    ) {
      return footerContent.socialLinks.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 3. Check for social_links.platforms (alternative structure)
    if (
      footerContent?.social_links?.platforms &&
      Array.isArray(footerContent.social_links.platforms)
    ) {
      return footerContent.social_links.platforms.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    // 4. Check for social_links as array
    if (footerContent?.social_links && Array.isArray(footerContent.social_links)) {
      return footerContent.social_links.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }

    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.socialLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const title = footerContent?.quickLinksTitle;
    if (typeof title === 'string') {
      return title;
    } else if (title && typeof title === 'object' && title.text) {
      return title.text;
    }
    return 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const copyright = footerContent?.copyright;
    if (typeof copyright === 'string') {
      return copyright;
    } else if (copyright && typeof copyright === 'object' && copyright.text) {
      return copyright.text;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo object if present, falling back to header logo or a default.
   * @example
   * this.etFooterLogo()
   * { text: 'way', image: '' }
   * @returns {{ text?: string; image?: string } | undefined} The footer logo object with optional text and image fields, or undefined if none is available.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  /**
   * Get more information section element for selection
   */
  getMoreInformationSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'services' ||
        el.id === 'more_information_section' ||
        (el as any).originalKey === 'more_information_section'
    );
  }

  /**
   * Get more information section services
   */
  getMoreInformationServices(): any[] {
    if (
      this.moreInformationSectionData?.services &&
      Array.isArray(this.moreInformationSectionData.services)
    ) {
      return this.moreInformationSectionData.services;
    }
    return [];
  }

  /**
   * Get more information section title
   */
  getMoreInformationTitle(): string {
    if (this.moreInformationSectionData?.title?.label) {
      return this.moreInformationSectionData.title.label;
    }
    return 'More Information';
  }

  /**
   * Get more information section subtitle
   */
  getMoreInformationSubtitle(): string {
    if (this.moreInformationSectionData?.subtitle?.label) {
      return this.moreInformationSectionData.subtitle.label;
    }
    return 'Explore a quick overview of our car wash specialties.';
  }

  /**
   * Get more information section background color
   */
  getMoreInformationBackgroundColor(): string {
    const element = this.getMoreInformationSectionElement();
    const content = element?.content as any;
    if (content?.backgroundColor) {
      return content.backgroundColor;
    }
    if (this.moreInformationSectionData?.layout?.background_color) {
      return this.moreInformationSectionData.layout.background_color;
    }
    return '#FFFFFF'; // Default white background from Figma
  }

  /**
   * Handle a click event to trigger header behavior unless the click originated from navigation, CTA, or mobile menu elements.
   * @example
   * nHeaderClick(new MouseEvent('click'))
   * undefined
   * @param {{Event}} {{event}} - The click event to process.
   * @returns {{void}} No return value; may stop propagation for specific targets or invoke the header click handler.
   */
  onHeaderClick(event: Event): void {
    // Don't propagate if clicking on navigation items, CTA button, or hamburger menu
    const target = event.target as HTMLElement;
    if (
      target.closest('.nav-link') ||
      target.closest('.book-wash-btn') ||
      target.closest('.hamburger-menu') ||
      target.closest('.mobile-menu-overlay') ||
      target.closest('.mobile-menu-drawer')
    ) {
      event.stopPropagation();
      return;
    }

    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  /**
   * Handle a footer click in the preview: stop propagation, ensure the footer element has type/id set to 'footer', and forward the click to element handler.
   * @example
   * nFooterClick(new MouseEvent('click'))
   * undefined
   * @param {Event} event - The click event triggered on the preview footer.
   * @returns {void} Stops propagation, normalizes the footer element, delegates the click, and returns nothing.
   **/
  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      // Ensure footer element has correct type and id
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  onMoreInformationSectionClick(event: Event): void {
    event.stopPropagation();
    const moreInfoElement = this.getMoreInformationSectionElement();
    if (moreInfoElement) {
      this.onElementClick(moreInfoElement);
    }
  }

  onBuyNowClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  onSubscribeClick(pkg: Package, event: Event): void {
    event.stopPropagation();
    this.selectedPackageForModal = pkg;
    this.showPackageSelectionModal = true;
  }

  onClosePackageModal(): void {
    this.showPackageSelectionModal = false;
    this.selectedPackageForModal = null;
  }

  onPackageSelected(selected: SelectedPackage): void {
    this.logger.debug('Package selected:', selected);
    // Handle the selected package here
    // You can emit an event or perform any action needed
    // Don't close the modal automatically - let user close it manually or proceed to next step
    // this.showPackageSelectionModal = false;
    // this.selectedPackageForModal = null;
  }

  getPackagesForModal(): Package[] {
    if (this.selectedPackageForModal) {
      return [this.selectedPackageForModal];
    }
    return this.packages;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
